import React, { useState, useRef, useCallback, useEffect } from 'react';
import AntennaSelectionHero, {
	IAntennaSelectionHeroProps,
} from '../AntennaSelectionHero';
import AntennaSelectionStepsProgress, {
	IAntennaSelectionStepsProgressProps,
} from '../AntennaSelectionStepsProgress';
import AntennaSelectionRadioOptions, {
	IAntennaSelectionRadioOptionsProps,
} from '../AntennaSelectionRadioOptions';
import AntennaSelectionGraph, {
	IAntennaSelectionGraphProps,
} from '../AntennaSelectionGraph';
import AntennaSelectionTables, {
	IAntennaSelectionTablesProps,
} from '../AntennaSelectionTables';
import AntennaSelectionToggleSwitch, {
	IAntennaSelectionToggleSwitchProps,
} from '../AntennaSelectionToggleSwitch';
import AntennaSelectionGainValue, {
	IAntennaSelectionGainValueProps,
} from '../AntennaSelectionGainValue';
import AntennaSelectionNextSteps, {
	IAntennaSelectionNextStepsProps,
} from '../AntennaSelectionNextSteps';
import clone from 'lodash/clone';
import Loading from 'tbk-components/src/components/Loading';

export interface IAntennaSelectionProps {
	hero?: IAntennaSelectionHeroProps;
	steps?: IAntennaSelectionStepsProgressProps;
	bandTypes?: IAntennaSelectionRadioOptionsProps;
	frequenciesGraph?: IAntennaSelectionGraphProps;
	frequencyData?: IAntennaSelectionTablesProps;
	lBandToggle?: IAntennaSelectionToggleSwitchProps;
	productTechTypes?: IAntennaSelectionRadioOptionsProps;
	mountTypes?: IAntennaSelectionRadioOptionsProps;
	amplifierGain?: IAntennaSelectionGainValueProps;
	passiveAntennaToggle?: IAntennaSelectionToggleSwitchProps;
	productUrl?: string;
	nextStepButtons?: IAntennaSelectionNextStepsProps[];
}

const concatSearchQuery = (query, url) => {
	if (typeof query === 'object' && query !== null) {
		const _q = clone(query);

		if (query.lBand === true) {
			if (query.by_coverage) {
				_q.by_coverage += ',L-band correction services';
			} else {
				_q.by_coverage = 'L-band correction services';
			}
		} else if (query.lBand === false) {
			if (_q.by_coverage) {
				_q.by_coverage = _q.by_coverage.replace(
					'L-band correction services',
					'',
				);
			}
		}

		if (query.passiveAntenna === true) {
			if (query['amplifier_gain=minimum-gain__']) {
				_q['amplifier_gain=minimum-gain__'] += ',passive-only';
			} else {
				_q['amplifier_gain=minimum-gain__'] = 'passive-only';
			}
		} else if (query.passiveAntenna === false) {
			if (
				_q['amplifier_gain=minimum-gain__']
					.toString()
					.includes('passive-only')
			) {
				_q['amplifier_gain=minimum-gain__'] = _q[
					'amplifier_gain=minimum-gain__'
				].replace('passive-only', '');
			}
		}

		delete _q['lBand'];
		delete _q['passiveAntenna'];

		const arr = Object.keys(_q).map((item) => {
			return `${item}=${_q[item]}`;
		});
		return `${url}${arr.join('&')}`;
	} else {
		return url;
	}
};

/**
 * Antenna Selection Guide
 * @block
 */
const AntennaSelection: React.FC<IAntennaSelectionProps> = ({
	hero,
	steps,
	bandTypes,
	frequenciesGraph,
	frequencyData,
	lBandToggle,
	productTechTypes,
	mountTypes,
	amplifierGain,
	passiveAntennaToggle,
	productUrl = '/products/gnss-products/#!page=1&',
	nextStepButtons = [],
}) => {
	const [currentStep, setCurrentStep] = useState(0);
	const [searchQuery, setSearchQuery] = useState(null);
	const [highlightedF, setHighlightedF] = useState([]);
	const [optionalF, setOptionalF] = useState([]);

	const stepsRef = useRef(null);

	const scrollToSteps = useCallback(() => {
		if (stepsRef.current) {
			stepsRef.current.scrollIntoView({
				behavior: 'smooth',
				block: 'start',
			});
		}
	}, [currentStep]);

	const step1 = () => (
		<>
			{bandTypes?.options?.length > 0 && (
				<AntennaSelectionRadioOptions
					{...bandTypes}
					updateQuery={(query) =>
						setSearchQuery({
							...searchQuery,
							by_coverage: query,
						})
					}
					updateHighlightedF={(f) => setHighlightedF(f)}
					updateOptionalF={(f) => setOptionalF(f)}
				/>
			)}
			{frequenciesGraph?.graphData?.length > 0 && (
				<AntennaSelectionGraph
					{...frequenciesGraph}
					highlightedFrequencies={highlightedF}
					optionalFrequencies={optionalF}
				/>
			)}
			{frequencyData?.tables?.length > 0 && (
				<AntennaSelectionTables {...frequencyData} />
			)}
		</>
	);
	const step2 = () => (
		<>
			{productTechTypes?.options?.length > 0 && (
				<AntennaSelectionRadioOptions
					{...productTechTypes}
					updateQuery={(query) =>
						setSearchQuery({
							...searchQuery,
							product_technology: query,
						})
					}
				/>
			)}
		</>
	);
	const step3 = () => (
		<>
			{mountTypes?.options?.length > 0 && (
				<AntennaSelectionRadioOptions
					{...mountTypes}
					updateQuery={(query) =>
						setSearchQuery({
							...searchQuery,
							mount_type: query,
						})
					}
				/>
			)}
		</>
	);
	const stepContent = [step1, step2, step3];

	return (
		<>
			{hero?.heading && <AntennaSelectionHero {...hero} />}
			<div ref={stepsRef}>
				{steps?.steps?.length > 0 && (
					<AntennaSelectionStepsProgress
						{...steps}
						current={currentStep}
					/>
				)}
			</div>
			{currentStep < steps?.steps?.length ? (
				<div className="antenna-section__step-container">
					{stepContent[currentStep]()}
				</div>
			) : (
				<Loading />
			)}
			{nextStepButtons?.length > 0 && (
				<>
					{currentStep === steps?.steps?.length - 1 ? (
						<AntennaSelectionNextSteps
							{...nextStepButtons[currentStep]}
							nextStepButton={{
								...nextStepButtons[currentStep].nextStepButton,
								href: concatSearchQuery(
									searchQuery,
									productUrl,
								),
							}}
							current={currentStep}
							updateCurrent={(increase) => {
								if (increase) setCurrentStep((c) => c + 1);
								if (!increase) setCurrentStep((c) => c - 1);
								scrollToSteps();
							}}
						/>
					) : (
						<AntennaSelectionNextSteps
							{...nextStepButtons[currentStep]}
							nextStepButton={{
								label: `${
									nextStepButtons[currentStep].nextStepButton
										.label
								} (${currentStep + 2}/${steps?.steps?.length})`,
							}}
							current={currentStep}
							updateCurrent={(increase) => {
								if (increase) setCurrentStep((c) => c + 1);
								if (!increase) setCurrentStep((c) => c - 1);
								scrollToSteps();
							}}
						/>
					)}
				</>
			)}
		</>
	);
};

export default AntennaSelection;
